import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

const POLL_MS = 15_000;
const READ_KEY = 'lumi.missedCallsLastRead';

/**
 * Notification bell with a count of unread missed calls. "Unread" means
 * missed calls whose startedAt is newer than the lastRead timestamp stored
 * in localStorage (per-browser, per-user). Clicking the bell opens the
 * dropdown and marks all currently-shown calls as read.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [lastRead, setLastRead] = useState<string>(() =>
    typeof window === 'undefined' ? '' : (window.localStorage.getItem(READ_KEY) ?? ''),
  );
  const ref = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['missed-calls'],
    queryFn: () => api.missedCalls().then(r => r.missed),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  const calls = query.data ?? [];
  const unread = calls.filter(c => c.startedAt > lastRead);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function toggle() {
    if (!open && unread.length > 0) {
      // Mark all currently-visible missed calls as read.
      const newest = calls[0]?.startedAt;
      if (newest) {
        window.localStorage.setItem(READ_KEY, newest);
        setLastRead(newest);
      }
    }
    setOpen(o => !o);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className="relative text-slate-500 hover:text-slate-900 p-1.5 rounded-md"
        aria-label="Missed calls"
        title={unread.length > 0 ? `${unread.length} new missed call${unread.length === 1 ? '' : 's'}` : 'No new missed calls'}
      >
        <BellIcon />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center font-semibold">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Missed calls</h3>
            <p className="text-xs text-slate-500">Last 24 hours</p>
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {calls.length === 0 && (
              <li className="px-3 py-4 text-xs text-slate-400 text-center">No missed calls.</li>
            )}
            {calls.map(c => (
              <li key={c.callSid} className="px-3 py-2 flex items-center gap-2 text-sm">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                <span className="font-mono text-slate-800">{formatNumber(c.from)}</span>
                <span className="flex-1 text-xs text-slate-500 text-right">{formatRelative(c.startedAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function formatNumber(raw: string): string {
  if (!raw) return '—';
  if (raw.startsWith('+61') && raw.length === 12) {
    return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`;
  }
  if (raw.startsWith('+1') && raw.length === 12) {
    return `${raw.slice(0, 2)} (${raw.slice(2, 5)}) ${raw.slice(5, 8)}-${raw.slice(8)}`;
  }
  return raw;
}

function formatRelative(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}
